import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import {
  DailySummaryDto,
  TriggerDTO,
} from '@peartherapeutics/hylia-api-client';
import getHyliaApiClient from 'clients/hylia-api';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { DashboardContext } from 'components/screens/PatientDashboard/context';
import TriggerChart from 'components/screens/PatientDashboard/TriggerChart';
import { PRESCRIPTION_STATUS } from 'utils/prescription';
import moment from 'moment';
import { isInDateRange } from 'utils/dateTime';
import { getAverageTriggers } from '../utils';
import IntensityChart from './IntensityChart';
import StatusBar from './StatusBar';
import styles from './styles.module.scss';
import {
  selectAverageIntensity,
  selectFilteredDates,
  selectWithTriggers,
} from './utils/selectors';

const Cravings = () => {
  const {
    patientId,
    selectedAccessRight,
    dailySummaries,
    setDailySummaries,
    patientInfo,
  } = useContext(DashboardContext);
  const {
    status: arStatus,
    id: accessRightId,
    timezone,
  } = selectedAccessRight!;
  const minDate =
    patientInfo!.accessRights.length > 0
      ? patientInfo!.accessRights[patientInfo!.accessRights.length - 1]
          .startDate
      : new Date().toISOString();
  const maxDate =
    patientInfo!.accessRights.length > 0
      ? patientInfo!.accessRights[0].endDate
      : new Date().toISOString();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [cravingsCount, setCravingsCount] = useState<number>(0);
  const [thisWeeksAvgIntensity, setThisWeeksAvgIntensity] = useState<number>(0);
  const [triggersArray, setTriggersArray] = useState<TriggerDTO[]>([]);
  const [triggerFiltered, setTriggerFiltered] = useState<DailySummaryDto[]>(
    selectWithTriggers(dailySummaries ?? []),
  );
  const [chartStart, setChartStart] = useState<string>(minDate);
  const [chartEnd, setChartEnd] = useState<string>(maxDate);

  /** sets cache for dailySummaries and sets up the triggerFiltered summaries */
  const getSubstanceUseData = useCallback(
    async (patientId: string, accessRightId: string) => {
      try {
        // patientId and accessRightId are validated in useEffect before this call is made
        if (!dailySummaries) {
          const apiClient = await getHyliaApiClient();

          const { dailySummaries } = await apiClient
            .getPatientCravingAndUseSummary(patientId, accessRightId)
            .then((res) => res.data);

          /** set global cache of dailySummaries */
          setDailySummaries(dailySummaries);

          /** set (component) state for ONLY summaries containing trigger data. */
          setTriggerFiltered(selectWithTriggers(dailySummaries));
        }
      } catch (e) {
        ErrorInterceptor.error(e);
      } finally {
        setIsLoaded(true);
      }
    },
    [setTriggerFiltered, setDailySummaries, dailySummaries],
  );

  /** for DailySummaries initial load and "cache"  */
  useEffect(() => {
    if (patientId && accessRightId) {
      getSubstanceUseData(patientId, accessRightId);
    }
  }, [patientId, accessRightId, getSubstanceUseData]);

  /** for the StatusBar */
  useEffect(() => {
    if (triggerFiltered) {
      // TODO (MW)[WB-3663]: move to 'dateFiltered' state to share with other components
      const dateFiltered = selectFilteredDates({
        dailies: triggerFiltered,
        keyDate: chartStart,
        endDate: chartEnd,
      });
      setCravingsCount(dateFiltered.length);
      setThisWeeksAvgIntensity(selectAverageIntensity(dateFiltered));
    }
  }, [
    triggerFiltered,
    chartStart,
    chartEnd,
    setCravingsCount,
    setThisWeeksAvgIntensity,
  ]);

  /** for the Triggers chart */
  useEffect(() => {
    if (triggerFiltered) {
      const dateSummaries = triggerFiltered?.filter(
        ({ date }) =>
          date &&
          isInDateRange(
            moment(date).utc(),
            moment(chartStart).utc(),
            moment(chartEnd).utc(),
          ),
      );
      setTriggersArray(getAverageTriggers(dateSummaries!));
    }
  }, [chartStart, chartEnd, triggerFiltered]);

  const onDateRangeChanged = useCallback(
    (start: string, end: string) => {
      setChartStart(start);
      setChartEnd(end);
    },
    [setChartEnd, setChartStart],
  );

  return (
    <div data-testid={`${isLoaded ? 'cravings-loaded' : 'cravings-loading'}`}>
      {arStatus === PRESCRIPTION_STATUS.STARTED && (
        <StatusBar
          averageIntensityThisWeek={thisWeeksAvgIntensity}
          cravingsThisWeek={cravingsCount}
        />
      )}
      <Grid className={styles.charts} container data-testid="charts">
        <Grid item md={8}>
          <IntensityChart
            minDate={minDate}
            maxDate={maxDate}
            timezone={timezone}
            dailies={dailySummaries}
            onDateRangeChanged={onDateRangeChanged}
          />
        </Grid>
        <Grid item md={4}>
          <div data-testid="full-trigger-chart">
            <div data-testid="trigger-chart-header" className={styles.header}>
              <div className={styles.name}>Triggers</div>
            </div>
            <TriggerChart triggers={triggersArray} />
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default Cravings;
